import React, { useMemo, useState } from 'react';

// D6: ödev etiket seçici — MEB müfredat kazanımı (learning_outcomes) ve/veya
// kitap konusu (subject_units) ekler. The two vocabularies stay visibly and
// semantically separate. CreateAssignmentModal (yeni ödev) ve
// öğretmen Ödevler sayfası (mevcut ödevi düzenleme) tarafından paylaşılır.
// Options come from GET /teacher/assignment-outcome-options.

export interface OutcomeOptions {
  grade: number | null;
  meb: { id: number; code: string; text: string; subject_name: string; unit_title: string | null }[];
  book: { id: number; title: string }[];
  suggested_book: { id: number; title: string }[];
}

interface Props {
  options: OutcomeOptions;
  mebIds: number[];
  bookUnitIds: number[];
  onChange: (sel: { mebIds: number[]; bookUnitIds: number[] }) => void;
}

const OutcomePicker: React.FC<Props> = ({ options, mebIds, bookUnitIds, onChange }) => {
  const [source, setSource] = useState<'meb' | 'book'>('meb');
  const [q, setQ] = useState('');

  const suggestedIds = useMemo(() => new Set(options.suggested_book.map(s => s.id)), [options.suggested_book]);

  const toggleMeb = (id: number) =>
    onChange({ mebIds: mebIds.includes(id) ? mebIds.filter(x => x !== id) : [...mebIds, id], bookUnitIds });
  const toggleBook = (id: number) =>
    onChange({ mebIds, bookUnitIds: bookUnitIds.includes(id) ? bookUnitIds.filter(x => x !== id) : [...bookUnitIds, id] });

  const t = q.trim().toLocaleLowerCase('tr');
  const mebList = t
    ? options.meb.filter(o => o.text.toLocaleLowerCase('tr').includes(t) || o.code.toLocaleLowerCase('tr').includes(t))
    : options.meb;
  // Suggested (section-linked) book topics float to the top of the book list.
  const bookSorted = useMemo(() => {
    const list = t ? options.book.filter(o => o.title.toLocaleLowerCase('tr').includes(t)) : options.book;
    return [...list].sort((a, b) => Number(suggestedIds.has(b.id)) - Number(suggestedIds.has(a.id)));
  }, [options.book, t, suggestedIds]);

  return (
    <div className="rounded-[12px] border border-[#e5e5e5] bg-white p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex gap-2">
          {(['book', 'meb'] as const).map(s => (
            <button
              key={s}
              type="button"
              onClick={() => setSource(s)}
              className="rounded-full px-3 py-1 text-[12px] font-medium transition"
              style={source === s ? { background: '#e5599b', color: '#fff' } : { background: '#f3f4f6', color: '#555' }}
            >
              {s === 'meb' ? 'MEB Kazanımları' : 'Kitap Konuları'}
            </button>
          ))}
        </div>
        <span className="text-[12px] text-gray-500">
          {mebIds.length + bookUnitIds.length > 0
            ? `${mebIds.length} MEB kazanımı · ${bookUnitIds.length} kitap konusu`
            : 'Opsiyonel'}
        </span>
      </div>

      <input
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder={source === 'meb' ? 'MEB kazanımı ara…' : 'Kitap konusu ara…'}
        className="w-full rounded-[10px] border border-[#e5e5e5] px-3 py-2 text-[13px] mb-2 focus:outline-none focus:border-[#e5599b]"
      />

      <div className="max-h-44 overflow-y-auto space-y-1 pr-1">
        {source === 'meb' ? (
          mebList.length === 0 ? (
            <p className="text-[12px] text-gray-400 py-2">
              {options.meb.length === 0 ? 'Bu sınıf/ders için MEB kazanımı bulunamadı.' : 'Aramayla eşleşen kazanım yok.'}
            </p>
          ) : (
            mebList.map(o => (
              <label key={o.id} className="flex items-start gap-2 cursor-pointer rounded-[8px] px-2 py-1.5 hover:bg-[#faf5f8]">
                <input
                  type="checkbox"
                  checked={mebIds.includes(o.id)}
                  onChange={() => toggleMeb(o.id)}
                  className="mt-0.5 accent-[#e5599b]"
                />
                <span className="text-[12px] leading-snug text-gray-700">
                  <span className="font-mono text-[11px] text-gray-500">{o.code}</span> {o.text}
                </span>
              </label>
            ))
          )
        ) : bookSorted.length === 0 ? (
          <p className="text-[12px] text-gray-400 py-2">
            {options.book.length === 0 ? 'Branşınız için kitap konusu bulunamadı.' : 'Aramayla eşleşen konu yok.'}
          </p>
        ) : (
          bookSorted.map(o => (
            <label key={o.id} className="flex items-start gap-2 cursor-pointer rounded-[8px] px-2 py-1.5 hover:bg-[#faf5f8]">
              <input
                type="checkbox"
                checked={bookUnitIds.includes(o.id)}
                onChange={() => toggleBook(o.id)}
                className="mt-0.5 accent-[#e5599b]"
              />
              <span className="text-[12px] leading-snug text-gray-700">
                {o.title}
                {suggestedIds.has(o.id) && (
                  <span className="ml-1 rounded-full bg-[#fdeef5] px-1.5 py-0.5 text-[10px] text-[#e5599b]">bu bölümle ilişkili</span>
                )}
              </span>
            </label>
          ))
        )}
      </div>
    </div>
  );
};

export default OutcomePicker;
